import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, isValid, parseISO } from "date-fns";
import { CalendarIcon, ArrowUpRight, ArrowDownLeft, FilterX, Search, Download } from "lucide-react";
import {
  Table,
  Pagination,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Stack,
  OverlayTrigger,
  Popover,
  PopoverBody,
  Container,
  Badge,
  Card
} from "react-bootstrap";
import { useAuth } from "./Home1";

// Import Layout Components
import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";

const BASE_API_URL = process.env.REACT_APP_API_URL;

const TransactionHistory = () => {
  const { user, token } = useAuth();
  const currentUserId = user?.id;
  const isAdmin = user?.id === "71";

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState(undefined);
  const [toDate, setToDate] = useState(undefined);

  // Helper function to format numbers
  const formatNumberWithCommas = (value, decimalPlaces = 0) => {
    if (value === null || value === undefined || value === "") return "0.00";
    const number = Number(value);
    if (isNaN(number)) return "0.00";
    return number.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  };

  const fetchData = useCallback(async (userId, authToken) => {
    if (!userId || !authToken) {
      setTransactions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const historyUrl = `${BASE_API_URL}/transaction-history`;
      const response = await axios.get(historyUrl, {
        params: { userId: userId },
        headers: { Authorization: `Bearer ${authToken}` },
        timeout: 15000,
      });
      
      const rawTransactions = response.data || [];
      const sortedTransactions = rawTransactions.sort((a, b) => {
        const dateA = a.tx_created ? new Date(a.tx_created) : new Date(0);
        const dateB = b.tx_created ? new Date(b.tx_created) : new Date(0);
        return dateB - dateA;
      });
      setTransactions(sortedTransactions);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load transaction history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentUserId, token);
  }, [currentUserId, token, fetchData]);

  // Filtering Logic
  const filteredTransactions = transactions.filter((tx) => {
    const txDate = tx.tx_created ? new Date(tx.tx_created) : null;
    let fromDateObj = fromDate ? new Date(fromDate) : null;
    if (fromDateObj) fromDateObj.setHours(0, 0, 0, 0);
    let toDateObj = toDate ? new Date(toDate) : null;
    if (toDateObj) toDateObj.setHours(23, 59, 59, 999);
    
    return (!fromDateObj || (txDate && txDate >= fromDateObj)) &&
           (!toDateObj || (txDate && txDate <= toDateObj));
  });

  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);
  const currentTransactions = filteredTransactions.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleResetDates = () => {
    setFromDate(undefined);
    setToDate(undefined);
    setCurrentPage(1);
  };

  const formatDateString = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return isValid(date) ? format(date, "dd MMM yyyy") : "Invalid Date";
  };

  const renderCalendarPopover = (date, onSelect, disabledDateLogic) => (
    <Popover id="popover-calendar">
      <PopoverBody>
        <Form.Control 
          type="date" 
          value={date ? format(date, "yyyy-MM-dd") : ""} 
          onChange={(e) => {
            const val = e.target.value ? new Date(e.target.value) : undefined;
            if (disabledDateLogic && disabledDateLogic(val)) return;
            onSelect(val);
            setCurrentPage(1);
          }} 
        />
      </PopoverBody>
    </Popover>
  );

  const renderContent = () => {
    if (loading) return (
      <div className="text-center py-5">
        <Spinner animation="grow" variant="primary" />
        <p className="mt-3 text-muted">Fetching your ledger...</p>
      </div>
    );

    if (error) return (
      <Alert variant="danger" className="text-center shadow-sm border-0">
        <p className="mb-2">{error}</p>
        <Button variant="outline-danger" size="sm" onClick={() => fetchData(currentUserId, token)}>Retry</Button>
      </Alert>
    );

    if (filteredTransactions.length === 0) return (
      <Card className="text-center p-5 border-0 shadow-sm bg-light">
        <FilterX size={48} className="mx-auto text-muted mb-3" />
        <h5 className="text-secondary">No records found</h5>
        <p className="small text-muted">Try adjusting your date range filters.</p>
        <Button variant="link" onClick={handleResetDates}>Clear all filters</Button>
      </Card>
    );

    return (
      <div className="bg-white rounded-4 shadow-sm border overflow-hidden">
        <Table hover responsive className="mb-0 align-middle">
          <thead className="bg-light border-bottom">
            <tr>
              <th className="px-4 py-3 text-secondary small fw-bold text-uppercase">Transaction Date</th>
              <th className="px-3 py-3 text-secondary small fw-bold text-uppercase text-center">Status</th>
              <th className="px-3 py-3 text-secondary small fw-bold text-uppercase text-end">Amount</th>
              <th className="px-3 py-3 text-secondary small fw-bold text-uppercase text-end">SMS Units</th>
              <th className="px-3 py-3 text-secondary small fw-bold text-uppercase text-end">New Balance</th>
              <th className="px-4 py-3 text-secondary small fw-bold text-uppercase">Description</th>
            </tr>
          </thead>
          <tbody>
            {currentTransactions.map((tx, index) => {
              const isCredit = Number(tx.tx_amount) > 0;
              return (
                <tr key={tx.tx_id || index} className="border-bottom">
                  <td className="px-4 py-3">
                    <div className="fw-bold text-dark">{formatDateString(tx.tx_created)}</div>
                    <div className="text-muted" style={{ fontSize: '11px' }}>
                      {tx.tx_created ? format(new Date(tx.tx_created), "hh:mm a") : ""}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <Badge 
                      bg={isCredit ? "success" : "light"} 
                      text={isCredit ? "white" : "danger"}
                      className="rounded-pill px-3 py-2 fw-medium border"
                      style={{ fontSize: '10px' }}
                    >
                      {isCredit ? <><ArrowUpRight size={10} className="me-1"/> CREDIT</> : <><ArrowDownLeft size={10} className="me-1"/> DEBIT</>}
                    </Badge>
                  </td>
                  <td className={`px-3 py-3 text-end fw-bold ${isCredit ? 'text-success' : 'text-danger'}`}>
                    {isCredit ? '+' : ''}₦{formatNumberWithCommas(tx.tx_amount, 2)}
                  </td>
                  <td className="px-3 py-3 text-end text-dark fw-medium">
                    {formatNumberWithCommas(tx.tx_unit, 0)} <span className="text-muted small">units</span>
                  </td>
                  <td className="px-3 py-3 text-end fw-bold text-primary">
                    ₦{formatNumberWithCommas(tx.tx_balance_after, 2)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-muted small d-block text-truncate" style={{ maxWidth: '180px' }}>
                      {tx.tx_memo || "N/A"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {totalPages > 1 && (
          <div className="p-3 border-top bg-light d-flex justify-content-center">
            <Pagination size="sm" className="mb-0">
               <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
               <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} />
               <Pagination.Item active>{currentPage}</Pagination.Item>
               <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} />
               <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
            </Pagination>
          </div>
        )}
      </div>
    );
  };

  return (
    <Container className="pb-5">
      <Header user={user} />
      <Row className="mt-4 px-md-3">
        <Col md="3">
          <Logout Menu="Login Menu" user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {isAdmin && <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />}
        </Col>
        <Col md="9">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
            <h3 className="fw-bold text-primary d-flex align-items-center mb-3 mb-md-0">
              <Search className="me-2" size={24} /> Transaction History
            </h3>
            
            <Stack direction="horizontal" gap={2}>
              <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={renderCalendarPopover(fromDate, setFromDate, (d) => toDate && d > toDate)}>
                <Button variant="white" className="border shadow-sm btn-sm d-flex align-items-center">
                  <CalendarIcon size={14} className="me-2 text-primary" />
                  {fromDate ? format(fromDate, "dd MMM") : "Start Date"}
                </Button>
              </OverlayTrigger>

              <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={renderCalendarPopover(toDate, setToDate, (d) => fromDate && d < fromDate)}>
                <Button variant="white" className="border shadow-sm btn-sm d-flex align-items-center">
                  <CalendarIcon size={14} className="me-2 text-primary" />
                  {toDate ? format(toDate, "dd MMM") : "End Date"}
                </Button>
              </OverlayTrigger>

              {(fromDate || toDate) && (
                <Button variant="outline-danger" size="sm" onClick={handleResetDates} title="Clear Filters">
                  <FilterX size={14} />
                </Button>
              )}
            </Stack>
          </div>

          {renderContent()}
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default TransactionHistory;